import type { FieldEntry, OperatorMeta, PredicateValue } from './types'
import { StringInput } from './ValueInputs/StringInput'
import { NumberInput } from './ValueInputs/NumberInput'
import { DatePicker } from './ValueInputs/DatePicker'
import { MultiInput } from './ValueInputs/MultiInput'
import { PlaceholderPicker } from './ValueInputs/PlaceholderPicker'

export function ValueInput({
  field, op, value, onChange, placeholders,
}: {
  field: FieldEntry | null
  op: OperatorMeta | null
  value: PredicateValue
  onChange: (v: PredicateValue) => void
  placeholders: string[]
}) {
  if (!field || !op || op.novalue) return null

  // Placeholder mode - if value is currently a @-string, show placeholder picker
  if (typeof value === 'string' && value.startsWith('@')) {
    return (
      <PlaceholderPicker
        value={value}
        onChange={onChange}
        options={placeholders}
        onClear={() => onChange('')}
      />
    )
  }

  // Toggle button to switch to placeholder mode
  const togglePlaceholder = (
    <button
      type="button"
      onClick={() => onChange(placeholders[0] ?? '@me')}
      className="text-xs text-violet-700 hover:text-violet-900 px-1 whitespace-nowrap"
      title="Użyj zmiennej kontekstu (@me, @my_team_coverage, ...)"
    >@var</button>
  )

  if (op.multi) {
    return (
      <div className="flex items-center gap-1 flex-1">
        <MultiInput value={value} onChange={onChange as (v: string[]) => void} />
        {togglePlaceholder}
      </div>
    )
  }

  let input: React.ReactNode
  switch (field.type) {
    case 'number':
      input = <NumberInput value={value as number | string} onChange={onChange as (v: number) => void} />
      break
    case 'date':
      input = <DatePicker value={value as string} onChange={onChange as (v: string) => void} />
      break
    case 'boolean':
      input = null
      break
    default:
      input = <StringInput value={value as string} onChange={onChange as (v: string) => void} />
  }

  return <div className="flex items-center gap-1 flex-1">{input}{togglePlaceholder}</div>
}
